import { useEffect, useCallback } from 'react';

export const useContentProtection = (enabled: boolean = true) => {
  // Disable right-click context menu
  const disableRightClick = useCallback((e: MouseEvent) => {
    e.preventDefault();
    return false;
  }, []);

  // Disable text selection
  const disableTextSelection = useCallback((e: Event) => {
    e.preventDefault();
    return false;
  }, []);

  // Disable keyboard shortcuts for copy, cut, paste, save, print
  const disableKeyboardShortcuts = useCallback((e: KeyboardEvent) => {
    const isCtrlOrCmd = e.ctrlKey || e.metaKey;
    
    if (isCtrlOrCmd) {
      switch (e.key.toLowerCase()) {
        case 'c': // Copy
        case 'x': // Cut
        case 'v': // Paste
        case 'a': // Select all
        case 's': // Save
        case 'p': // Print
        case 'u': // View source
          e.preventDefault();
          return false;
      }
    }
    
    // Disable F12 key (Developer Tools)
    if (e.key === 'F12') {
      e.preventDefault();
      return false;
    }
    
    // Disable Ctrl+Shift+I (Developer Tools)
    if (isCtrlOrCmd && e.shiftKey && e.key === 'I') {
      e.preventDefault();
      return false;
    }
    
    // Disable Ctrl+Shift+C (Developer Tools)
    if (isCtrlOrCmd && e.shiftKey && e.key === 'C') {
      e.preventDefault();
      return false;
    }
    
    // Disable Ctrl+Shift+J (Developer Tools)
    if (isCtrlOrCmd && e.shiftKey && e.key === 'J') {
      e.preventDefault();
      return false;
    }
  }, []);

  // Disable drag and drop
  const disableDragAndDrop = useCallback((e: DragEvent) => {
    e.preventDefault();
    return false;
  }, []);

  // Disable view source
  const disableViewSource = useCallback((e: Event) => {
    e.preventDefault();
    return false;
  }, []);

  // Add CSS to prevent text selection
  const addProtectionStyles = useCallback(() => {
    const styleId = 'content-protection-styles';
    
    // Remove existing styles if they exist
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }
    
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .content-protected {
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
        user-select: none !important;
        -webkit-touch-callout: none !important;
        -webkit-tap-highlight-color: transparent !important;
      }
      
      .content-protected * {
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
        user-select: none !important;
      }
      
      .content-protected img {
        pointer-events: none !important;
        -webkit-user-drag: none !important;
        -khtml-user-drag: none !important;
        -moz-user-drag: none !important;
        -o-user-drag: none !important;
        user-drag: none !important;
      }
      
      .content-protected a {
        pointer-events: auto !important;
      }
      
      .content-protected button {
        pointer-events: auto !important;
      }
      
      .content-protected input {
        pointer-events: auto !important;
        -webkit-user-select: text !important;
        -moz-user-select: text !important;
        -ms-user-select: text !important;
        user-select: text !important;
      }
      
      .content-protected textarea {
        pointer-events: auto !important;
        -webkit-user-select: text !important;
        -moz-user-select: text !important;
        -ms-user-select: text !important;
        user-select: text !important;
      }
    `;
    
    document.head.appendChild(style);
  }, []);

  // Remove protection styles
  const removeProtectionStyles = useCallback(() => {
    const style = document.getElementById('content-protection-styles');
    if (style) {
      style.remove();
    }
  }, []);

  // Add protection class to body
  const addProtectionClass = useCallback(() => {
    document.body.classList.add('content-protected');
  }, []);

  // Remove protection class from body
  const removeProtectionClass = useCallback(() => {
    document.body.classList.remove('content-protected');
  }, []);

  useEffect(() => {
    if (!enabled) {
      removeProtectionStyles();
      removeProtectionClass();
      return;
    }

    // Add protection styles
    addProtectionStyles();
    addProtectionClass();

    // Add event listeners
    document.addEventListener('contextmenu', disableRightClick);
    document.addEventListener('selectstart', disableTextSelection);
    document.addEventListener('keydown', disableKeyboardShortcuts);
    document.addEventListener('dragstart', disableDragAndDrop);
    document.addEventListener('drop', disableDragAndDrop);
    document.addEventListener('dragover', disableDragAndDrop);

    // Cleanup function
    return () => {
      document.removeEventListener('contextmenu', disableRightClick);
      document.removeEventListener('selectstart', disableTextSelection);
      document.removeEventListener('keydown', disableKeyboardShortcuts);
      document.removeEventListener('dragstart', disableDragAndDrop);
      document.removeEventListener('drop', disableDragAndDrop);
      document.removeEventListener('dragover', disableDragAndDrop);
      
      removeProtectionStyles();
      removeProtectionClass();
    };
  }, [
    enabled,
    disableRightClick,
    disableTextSelection,
    disableKeyboardShortcuts,
    disableDragAndDrop,
    disableViewSource,
    addProtectionStyles,
    removeProtectionStyles,
    addProtectionClass,
    removeProtectionClass
  ]);

  return {
    enableProtection: () => {
      addProtectionStyles();
      addProtectionClass();
    },
    disableProtection: () => {
      removeProtectionStyles();
      removeProtectionClass();
    }
  };
};
